import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDashboardStats, getEmployee, getCandidateSubmission } from '../services/employeeService';
import { EmployeeRecord } from '../types';
import { FiUsers, FiFileText, FiBriefcase, FiPlus, FiEye, FiSettings, FiDownload, FiAlertCircle } from 'react-icons/fi';
import { StatCardSkeleton, LoadingPill } from '../components/Skeleton';
import { useMinDelayed } from '../hooks/useMinDelayed';

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: number;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, color }) => (
  <div className="stat-card p-8 flex items-center">
    <div className={`p-4 rounded-xl mr-6 shadow-medium ${color}`}>
      {icon}
    </div>
    <div className="flex-1">
      <p className="text-sm font-semibold text-primary-600 uppercase tracking-wider mb-1">{title}</p>
      <p className="text-3xl font-bold text-primary-900">{value}</p>
    </div>
  </div>
);

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    newSubmissions: 0,
    underReview: 0,
    reworkRequested: 0,
    profilesVerified: 0,
    availableForHire: 0,
    hired: 0,
    rejected: 0,
    terminated: 0,
    blacklisted: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [candidateApplication, setCandidateApplication] = useState<EmployeeRecord | null>(null);
  const [applicationLoading, setApplicationLoading] = useState(true);

  const showStatsSkeleton = useMinDelayed(isLoading, 600);
  const showApplicationSkeleton = useMinDelayed(applicationLoading, 600);

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true);
      try {
        const stats = await getDashboardStats();
        setStats(stats);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchCandidateApplication = async () => {
      if (!user?.id) return;
      
      setApplicationLoading(true);
      try {
        // Use the candidate-specific endpoint to get their submission
        const response = await getCandidateSubmission();
        setCandidateApplication(response.data);
      } catch (error) {
        // No application found - that's okay for candidates
        setCandidateApplication(null);
      } finally {
        setApplicationLoading(false);
      }
    };

    if (user?.role && ['hr_clerk', 'manager', 'admin', 'viewer'].includes(user.role)) {
      fetchStats();
      setApplicationLoading(false);
    } else {
      // For candidates, fetch their application
      setIsLoading(false);
      fetchCandidateApplication();
    }
  }, [user?.id, user?.role]);

  // Skeleton grid that mirrors the StatCard layout — shown WHILE stats are
  // loading. Keeps the page header, sidebar, and surrounding chrome visible.
  const statsSkeleton = (
    <div className="mb-12">
      <div className="flex items-center gap-3 mb-4">
        <LoadingPill label="Fetching stats" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {Array.from({ length: 8 }).map((_, i) => (
          <StatCardSkeleton key={i} delay={i * 60} />
        ))}
      </div>
    </div>
  );

  // Role-based dashboard content
  const getDashboardContent = () => {
    switch (user?.role) {
      case 'viewer':
      case 'hr_clerk':
        return (
          <>
            {/* HR Stats Grid — skeleton while loading, real cards once stats arrive. */}
            {showStatsSkeleton ? statsSkeleton : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
                <StatCard
                  icon={<FiFileText className="text-white h-6 w-6" />}
                  title="New Submissions"
                  value={stats.newSubmissions}
                  color="bg-gradient-to-br from-accent-500 to-accent-600"
                />
                <StatCard
                  icon={<FiUsers className="text-white h-6 w-6" />}
                  title="Under Review"
                  value={stats.underReview}
                  color="bg-gradient-to-br from-brand-500 to-brand-600"
                />
                <StatCard
                  icon={<FiAlertCircle className="text-white h-6 w-6" />}
                  title="Rework Requested"
                  value={stats.reworkRequested}
                  color="bg-gradient-to-br from-warning-500 to-warning-600"
                />
                <StatCard
                  icon={<FiSettings className="text-white h-6 w-6" />}
                  title="Profiles Verified"
                  value={stats.profilesVerified}
                  color="bg-gradient-to-br from-primary-500 to-primary-600"
                />
                <StatCard
                  icon={<FiBriefcase className="text-white h-6 w-6" />}
                  title="Available for Hire"
                  value={stats.availableForHire}
                  color="bg-gradient-to-br from-success-400 to-success-500"
                />
                <StatCard
                  icon={<FiUsers className="text-white h-6 w-6" />}
                  title="Hired"
                  value={stats.hired}
                  color="bg-gradient-to-br from-success-500 to-success-600"
                />
                <StatCard
                  icon={<FiEye className="text-white h-6 w-6" />}
                  title="Rejected"
                  value={stats.rejected}
                  color="bg-gradient-to-br from-error-500 to-error-600"
                />
                <StatCard
                  icon={<FiEye className="text-white h-6 w-6" />}
                  title="Terminated"
                  value={stats.terminated}
                  color="bg-gradient-to-br from-gray-500 to-gray-600"
                />
              </div>
            )}

            {/* HR Quick Actions */}
            {/* <div>
              <h2 className="text-2xl font-bold text-primary-900 mb-6">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Link
                  to="/employees?status=new_submission"
                  className="stat-card group p-6 bg-gradient-to-br from-accent-600 to-accent-700 text-white hover:from-accent-700 hover:to-accent-800 transition-all duration-300"
                >
                  <div className="text-center">
                    <FiFileText className="h-10 w-10 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <p className="font-semibold text-lg">Review New Submissions</p>
                    <p className="text-sm opacity-90 mt-1">{stats.newSubmissions} pending</p>
                  </div>
                </Link>
                <Link
                  to="/employees"
                  className="stat-card group p-6 bg-gradient-to-br from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 transition-all duration-300"
                >
                  <div className="text-center">
                    <FiUsers className="h-10 w-10 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <p className="font-semibold text-lg">View All Records</p>
                    <p className="text-sm opacity-90 mt-1">Manage employee data</p>
                  </div>
                </Link>
                <Link
                  to="/register"
                  className="stat-card group p-6 bg-gradient-to-br from-success-600 to-success-700 text-white hover:from-success-700 hover:to-success-800 transition-all duration-300"
                >
                  <div className="text-center">
                    <FiPlus className="h-10 w-10 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <p className="font-semibold text-lg">Add New Employee</p>
                    <p className="text-sm opacity-90 mt-1">Create new record</p>
                  </div>
                </Link>
              </div>
            </div> */}
          </>
        );

      case 'manager':
        return (
          <>
            {/* Manager Stats Grid */}
            {showStatsSkeleton ? (
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <LoadingPill label="Fetching stats" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <StatCardSkeleton key={i} delay={i * 80} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <StatCard
                  icon={<FiBriefcase className="text-white h-7 w-7" />}
                  title="Available for Hire"
                  value={stats.availableForHire}
                  color="bg-gradient-to-br from-success-400 to-success-500"
                />
                <StatCard
                  icon={<FiUsers className="text-white h-7 w-7" />}
                  title="Hired"
                  value={stats.hired}
                  color="bg-gradient-to-br from-success-500 to-success-600"
                />
              </div>
            )}

            {/* Manager Quick Actions */}
            {/* <div>
              <h2 className="text-2xl font-bold text-primary-900 mb-6">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Link
                  to="/candidate-pool"
                  className="stat-card group p-6 bg-gradient-to-br from-brand-600 to-brand-700 text-white hover:from-brand-700 hover:to-brand-800 transition-all duration-300"
                >
                  <div className="text-center">
                    <FiUsers className="h-10 w-10 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <p className="font-semibold text-lg">Review Candidate Pool</p>
                    <p className="text-sm opacity-90 mt-1">{stats.candidates + stats.underReview} candidates available</p>
                  </div>
                </Link>
                <Link
                  to="/employees?status=current_employee"
                  className="stat-card group p-6 bg-gradient-to-br from-success-600 to-success-700 text-white hover:from-success-700 hover:to-success-800 transition-all duration-300"
                >
                  <div className="text-center">
                    <FiBriefcase className="h-10 w-10 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <p className="font-semibold text-lg">Current Employees</p>
                    <p className="text-sm opacity-90 mt-1">View {stats.currentEmployees} employees</p>
                  </div>
                </Link>
              </div>
            </div> */}
          </>
        );

      case 'admin':
        return (
          <>
            {/* Admin Stats Grid — skeleton while loading, real cards once stats arrive. */}
            {showStatsSkeleton ? statsSkeleton : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
                <StatCard
                  icon={<FiFileText className="text-white h-6 w-6" />}
                  title="New Submissions"
                  value={stats.newSubmissions}
                  color="bg-gradient-to-br from-accent-500 to-accent-600"
                />
                <StatCard
                  icon={<FiUsers className="text-white h-6 w-6" />}
                  title="Under Review"
                  value={stats.underReview}
                  color="bg-gradient-to-br from-brand-500 to-brand-600"
                />
                <StatCard
                  icon={<FiAlertCircle className="text-white h-6 w-6" />}
                  title="Rework Requested"
                  value={stats.reworkRequested}
                  color="bg-gradient-to-br from-warning-500 to-warning-600"
                />
                <StatCard
                  icon={<FiSettings className="text-white h-6 w-6" />}
                  title="Profiles Verified"
                  value={stats.profilesVerified}
                  color="bg-gradient-to-br from-primary-500 to-primary-600"
                />
                <StatCard
                  icon={<FiBriefcase className="text-white h-6 w-6" />}
                  title="Available for Hire"
                  value={stats.availableForHire}
                  color="bg-gradient-to-br from-success-400 to-success-500"
                />
                <StatCard
                  icon={<FiUsers className="text-white h-6 w-6" />}
                  title="Hired"
                  value={stats.hired}
                  color="bg-gradient-to-br from-success-500 to-success-600"
                />
                <StatCard
                  icon={<FiEye className="text-white h-6 w-6" />}
                  title="Rejected"
                  value={stats.rejected}
                  color="bg-gradient-to-br from-error-500 to-error-600"
                />
                <StatCard
                  icon={<FiEye className="text-white h-6 w-6" />}
                  title="Terminated"
                  value={stats.terminated}
                  color="bg-gradient-to-br from-gray-500 to-gray-600"
                />
                <StatCard
                  icon={<FiEye className="text-white h-6 w-6" />}
                  title="Blacklisted"
                  value={stats.blacklisted}
                  color="bg-gradient-to-br from-gray-700 to-gray-800"
                />
              </div>
            )}

            {/* Admin Quick Actions */}
            {/* <div>
              <h2 className="text-2xl font-bold text-primary-900 mb-6">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Link
                  to="/employees"
                  className="stat-card group p-6 bg-gradient-to-br from-accent-600 to-accent-700 text-white hover:from-accent-700 hover:to-accent-800 transition-all duration-300"
                >
                  <div className="text-center">
                    <FiUsers className="h-10 w-10 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <p className="font-semibold text-lg">Manage Records</p>
                    <p className="text-sm opacity-90 mt-1">All employee data</p>
                  </div>
                </Link>
                <Link
                  to="/export"
                  className="stat-card group p-6 bg-gradient-to-br from-brand-600 to-brand-700 text-white hover:from-brand-700 hover:to-brand-800 transition-all duration-300"
                >
                  <div className="text-center">
                    <FiDownload className="h-10 w-10 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <p className="font-semibold text-lg">Export Data</p>
                    <p className="text-sm opacity-90 mt-1">CSV/Excel exports</p>
                  </div>
                </Link>
                <Link
                  to="/settings"
                  className="stat-card group p-6 bg-gradient-to-br from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 transition-all duration-300"
                >
                  <div className="text-center">
                    <FiSettings className="h-10 w-10 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <p className="font-semibold text-lg">System Settings</p>
                    <p className="text-sm opacity-90 mt-1">Configure system</p>
                  </div>
                </Link>
                <Link
                  to="/register"
                  className="stat-card group p-6 bg-gradient-to-br from-success-600 to-success-700 text-white hover:from-success-700 hover:to-success-800 transition-all duration-300"
                >
                  <div className="text-center">
                    <FiPlus className="h-10 w-10 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <p className="font-semibold text-lg">Add Employee</p>
                    <p className="text-sm opacity-90 mt-1">Create new record</p>
                  </div>
                </Link>
              </div>
            </div> */}
          </>
        );

      default:
        // Candidate or default view
        const getStatusColor = (status: string) => {
          switch (status) {
            case 'new_submission':
              return 'bg-accent-100 text-accent-800 border border-accent-200';
            case 'candidate':
              return 'bg-warning-100 text-warning-800 border border-warning-200';
            case 'under_review':
              return 'bg-brand-100 text-brand-800 border border-brand-200';
            case 'requested_rework':
              return 'bg-orange-100 text-orange-800 border border-orange-200';
            case 'current_employee':
              return 'bg-success-100 text-success-800 border border-success-200';
            case 'rejected':
              return 'bg-error-100 text-error-800 border border-error-200';
            default:
              return 'bg-primary-100 text-primary-800 border border-primary-200';
          }
        };

        if (showApplicationSkeleton) {
          return (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <LoadingPill label="Loading your application" />
              </div>
              <div className="card">
                <div className="card-header">
                  <div className="skeleton-block h-6 w-48 rounded-md" aria-hidden="true" />
                </div>
                <div className="card-body space-y-4">
                  <div className="skeleton-block h-4 w-2/3 rounded-md" aria-hidden="true" />
                  <div className="skeleton-block h-4 w-1/2 rounded-md" aria-hidden="true" />
                  <div className="skeleton-block h-24 w-full rounded-md" aria-hidden="true" />
                </div>
              </div>
            </div>
          );
        }

        if (candidateApplication) {
          return (
            <>
              {/* Application Status */}
              <div className="card mb-8">
                <div className="card-header">
                  <h2 className="text-2xl font-bold text-primary-900">My Application</h2>
                </div>
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-primary-600 uppercase tracking-wider mb-2">Application Status</p>
                      <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(candidateApplication.status)}`}>
                        {candidateApplication.status.replace('_', ' ').toUpperCase()}
                      </span>
                      <p className="text-sm text-primary-500 mt-3 font-medium">
                        Reference: {candidateApplication.id}
                      </p>
                      {candidateApplication.status === 'requested_rework' && (
                        <div className="mt-4 flex items-center text-warning-700 bg-warning-50 border border-warning-200 rounded-lg p-3">
                          <FiAlertCircle className="mr-3 flex-shrink-0" size={18} />
                          <span className="text-sm font-medium">Action required: Please update your application</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-3 ml-6">
                      <Link
                        to="/my-application"
                        className="btn-outline"
                      >
                        <FiEye className="mr-2" size={16} />
                        View Details
                      </Link>
                      {candidateApplication.status === 'requested_rework' && (
                        <div className="inline-flex items-center px-4 py-2 bg-warning-100 text-warning-800 rounded-lg border border-warning-200">
                          <FiAlertCircle className="mr-2" size={16} />
                          <span className="text-sm font-semibold">Updates Required</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="text-center py-6">
                <p className="text-primary-600 font-medium leading-relaxed">
                  {candidateApplication.status === 'new_submission' && "Your application is being reviewed by our HR team."}
                  {candidateApplication.status === 'under_review' && "Your application is currently under review."}
                  {candidateApplication.status === 'requested_rework' && "Please update your application with the requested changes."}
                  {candidateApplication.status === 'profile_verified' && "Great! Your profile has been verified and you're in our system."}
                  {candidateApplication.status === 'available_for_hire' && "Excellent! You're now available for hire. We'll contact you about opportunities."}
                  {candidateApplication.status === 'hired' && "Congratulations! You've been hired. HR will contact you about next steps."}
                  {candidateApplication.status === 'current_employee' && "Welcome aboard! You're now an active employee."}
                  {candidateApplication.status === 'candidate' && "Congratulations! You've been moved to our candidate pool."}
                  {candidateApplication.status === 'rejected' && "We appreciate your interest. Please feel free to apply for other positions."}
                  {candidateApplication.status === 'terminated' && "Your employment has ended. Thank you for your service."}
                  {candidateApplication.status === 'blacklisted' && "Your application status has been updated."}
                </p>
              </div>
            </>
          );
        }

        return (
          <>
            <div className="text-center py-12">
              <h2 className="text-3xl font-bold text-gradient mb-4">Ready to Register?</h2>
              <p className="text-primary-600 mb-8 text-lg">Start your employment registration process today.</p>
              <Link
                to="/register"
                className="btn-primary inline-flex items-center text-lg px-8 py-4"
              >
                <FiPlus className="mr-3" size={20} />
                Start Registration
              </Link>
            </div>
          </>
        );
    }
  };

  const anyLoading = showStatsSkeleton || showApplicationSkeleton;

  return (
    <div>
      <div className="mb-12">
        <div className="flex items-center gap-4 mb-3 flex-wrap">
          <h1 className="text-4xl font-bold text-primary-900">Welcome, {user?.firstName}!</h1>
          {anyLoading && <LoadingPill label="Loading dashboard" />}
        </div>
        <p className="text-lg text-primary-600 font-medium">
          {user?.role === 'hr_clerk' && "Review and manage employee applications."}
          {user?.role === 'manager' && "Review candidates and manage hiring decisions."}
          {user?.role === 'admin' && "Oversee system operations and manage users."}
          {user?.role === 'viewer' && "View-only access to all system data."}
          {(!user?.role || user?.role === 'candidate') && "Start your employment application process."}
        </p>
      </div>

      {getDashboardContent()}
    </div>
  );
};

export default DashboardPage;
